import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../ui/use-toast';
import { COPY_FEEDBACK_MS } from './copy-feedback-duration';
import { copyToClipboard } from './copy-to-clipboard';

/**
 * Copy text, show toast, and drive CopyFeedbackIcon via `isCopied(feedbackKey)`.
 */
export function useCopyToClipboard() {
  const { toast } = useToast();
  const { t } = useTranslation('common');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    },
    [],
  );

  const isCopied = useCallback((feedbackKey: string) => copiedKey === feedbackKey, [copiedKey]);

  const copy = useCallback(
    async (text: string, successMessage?: string, feedbackKey?: string): Promise<boolean> => {
      if (!text) return false;
      try {
        await copyToClipboard(text);
        const key = feedbackKey ?? text;
        setCopiedKey(key);
        if (timerRef.current) {
          window.clearTimeout(timerRef.current);
        }
        timerRef.current = window.setTimeout(() => setCopiedKey(null), COPY_FEEDBACK_MS);
        toast(successMessage ?? t('common.copied'), 'success');
        return true;
      } catch {
        toast(t('common.copyFailed'), 'error');
        return false;
      }
    },
    [toast, t],
  );

  return { copy, isCopied };
}
